import { campaignKey } from "@/campaigns/campaign-key";
import type { CampaignSchema } from "@/types";
import { useAppStore } from "@/stores";

/**
 * How many unrevealed prizes a campaign has queued — the number badged on the
 * reveal surfaces and captioned in the reveal window.
 *
 * Sums every queued `prize_drop_won` notification's remaining prizes, and
 * floors at 1 so a win showing on the `unrevealedWins` seam is never captioned
 * "0 Prizes" — the seam is projected from the queue, but the two are separate
 * store writes, so a render can land between them.
 *
 * Shared so the in-game launcher button and the in-widget reveal card can't
 * disagree about the count.
 *
 * `campaign` is optional so callers can invoke the hook above their own early
 * returns (the launcher renders nothing until it has resolved a campaign).
 */
export function usePendingWinCount(campaign?: CampaignSchema): number {
  const queue = useAppStore((s) =>
    campaign ? s.wonQueueByCampaign[campaignKey(campaign)] : undefined
  );
  return (queue ?? []).reduce((sum, n) => sum + n.prizes.length, 0) || 1;
}
